import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuth } from '@contexts/AuthContext'
import { useConfig } from '@contexts/ConfigContext'
import { WebSocketProvider } from '@contexts/WebSocketContext'
import { PageTitleProvider } from '@contexts/PageTitleContext'

// Components
import LoadingSpinner from '@components/ui/LoadingSpinner'
import ErrorBoundary from '@components/ui/ErrorBoundary'
import DashboardLayout from '@components/layout/DashboardLayout'
import AdminLayout from '@components/admin-shell/AdminLayout'
import AdminRouteGuard from '@components/admin-shell/AdminRouteGuard'
import { LiveAnnouncerProvider } from '@components/ui/LiveAnnouncer'
import CommandPalette from '@components/ui/CommandPalette'
import { useRoutePageViews } from '@services/analyticsClient'
import PendingApprovalPage from '@pages/PendingApprovalPage'
import { DASHBOARD_FEATURES } from '@utils/featureFlags'
import { authenticationRedirect } from '@utils/x3ConnectAuthorization'
import { AdminSessionProvider } from '@contexts/AdminSessionContext'
import {
  getAdminCompatibilityRouteElements,
  getAdminRouteElements
} from '@/admin/routes'

// Lazy load pages for code splitting
const DashboardPage = React.lazy(() => import('@pages/DashboardPage'))
const CustomDashboardPage = React.lazy(() => import('@pages/CustomDashboardPage'))
const AlgorithmsPage = React.lazy(() => import('@pages/AlgorithmsPage'))
const AlgorithmDetailPage = React.lazy(() => import('@pages/AlgorithmDetailPage'))
const BacktestDetailPage = React.lazy(() => import('@pages/BacktestDetailPage'))
const ChartingPage = React.lazy(() => import('@pages/ChartingPage'))
const TradingHistoryPage = React.lazy(() => import('@pages/TradingHistoryPage'))
const AISignalsPage = React.lazy(() => import('@pages/AISignalsPage'))
const BrokersPage = React.lazy(() => import('@pages/BrokersPage'))
const SettingsPage = React.lazy(() => import('@pages/SettingsPage'))
const ProfilePage = React.lazy(() => import('@pages/ProfilePage'))
const ContractRolloverPage = React.lazy(() => import('@pages/ContractRolloverPage'))
const ArbitragePage = React.lazy(() => import('@pages/ArbitragePage'))
const PaperTradingPage = React.lazy(() => import('@pages/PaperTradingPage'))
const NotFoundPage = React.lazy(() => import('@pages/NotFoundPage'))
const JournalPage = React.lazy(() => import('@pages/JournalPage'))
const ScreenerPage = React.lazy(() => import('@pages/ScreenerPage'))
const OIAnalysisPage = React.lazy(() => import('@pages/OIAnalysisPage'))
const OptionsBuilderPage = React.lazy(() => import('@pages/OptionsBuilderPage'))
const MarketplacePage = React.lazy(() => import('@pages/MarketplacePage'))
const CodeEditorPage = React.lazy(() => import('@pages/CodeEditorPage'))
const AIChatPage = React.lazy(() => import('@pages/AIChatPage'))
const BacktestComparisonPage = React.lazy(() => import('@pages/BacktestComparisonPage'))
const AlgoComparisonPage = React.lazy(() => import('@pages/AlgoComparisonPage'))
const CorrelationMatrixPage = React.lazy(() => import('@pages/CorrelationMatrixPage'))
const AuditLogPage = React.lazy(() => import('@pages/AuditLogPage'))
const StatusPage = React.lazy(() => import('@pages/StatusPage'))
const CalculatorsPage = React.lazy(() => import('@pages/CalculatorsPage'))
const ApiPlaygroundPage = React.lazy(() => import('@pages/ApiPlaygroundPage'))
const OptimizationPage = React.lazy(() => import('@pages/OptimizationPage'))
const PortfolioPage = React.lazy(() => import('@pages/PortfolioPage'))
const CompliancePage = React.lazy(() => import('@pages/CompliancePage'))
const ConditionalOrdersPage = React.lazy(() => import('@pages/ConditionalOrdersPage'))
const WebhooksPage = React.lazy(() => import('@pages/WebhooksPage'))
const MCPManagementPage = React.lazy(() => import('@pages/MCPManagementPage'))
const X3ConnectAuthorizePage = React.lazy(
  () => import('@pages/X3ConnectAuthorizePage')
)
const TradingViewWebhookPage = React.lazy(() => import('@pages/TradingViewWebhookPage'))
const StrategyTemplatesPage = React.lazy(() => import('@pages/StrategyTemplatesPage'))
const AIUsagePage = React.lazy(() => import('@pages/AIUsagePage'))
const OptionsAnalyticsPage = React.lazy(() => import('@pages/OptionsAnalyticsPage'))

export function FeatureRoute({ feature, children }) {
  const { loading, isFeatureEnabled } = useConfig()

  if (loading) {
    return (
      <div className="flex min-h-48 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!isFeatureEnabled(feature)) {
    return <NotFoundPage />
  }

  return children
}

/**
 * Main Trading Dashboard App Component
 * Handles routing and authentication for trade.x3algo.com
 */
export function getLegacyAnalyticsDestination(role) {
  return ['admin', 'superAdmin'].includes(role)
    ? '/admin/analytics/legacy'
    : '/'
}

function App() {
  const { isAuthenticated, isLoading, user } = useAuth()
  useRoutePageViews()

  React.useEffect(() => {
    if (isLoading || !isAuthenticated) return
    const destination = new URL(window.location.href)
    if (destination.searchParams.get('sessionReplaced') !== '1') return
    destination.searchParams.delete('sessionReplaced')
    window.history.replaceState(
      window.history.state,
      '',
      `${destination.pathname}${destination.search}${destination.hash}`
    )
    toast('Your oldest active session was signed out to make room for this login.')
  }, [isAuthenticated, isLoading])

  // Show loading spinner during initial auth check
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Redirect to main site if not authenticated
  if (!isAuthenticated) {
    const loginUrl = import.meta.env.VITE_LOGIN_URL || 'http://localhost:3000/login'
    window.location.href = authenticationRedirect(loginUrl, window.location)
    return null
  }

  // Show pending approval page for non-active traders
  if (user?.role === 'trader' && user?.accountStatus !== 'active') {
    return <PendingApprovalPage />
  }

  return (
    <ErrorBoundary>
      <LiveAnnouncerProvider>
        <WebSocketProvider userId={user?._id}>
          <PageTitleProvider>
            <CommandPalette />
            <Routes>
              <Route
                path="connect/authorize"
                element={
                  <ErrorBoundary>
                    <X3ConnectAuthorizePage />
                  </ErrorBoundary>
                }
              />
              <Route element={<DashboardLayout />}>
                <Route index element={<ErrorBoundary><DashboardPage /></ErrorBoundary>} />
                <Route path="dashboard" element={<Navigate to="/" replace />} />
                <Route path="custom-dashboard" element={<CustomDashboardPage />} />
                <Route path="algorithms" element={<ErrorBoundary><AlgorithmsPage /></ErrorBoundary>} />
                <Route path="strategy-templates" element={<ErrorBoundary><StrategyTemplatesPage /></ErrorBoundary>} />
                <Route path="algorithms/:id" element={<ErrorBoundary><AlgorithmDetailPage /></ErrorBoundary>} />
                <Route path="algorithms/:id/optimize" element={<OptimizationPage />} />
                <Route path="backtests/:id" element={<ErrorBoundary><BacktestDetailPage /></ErrorBoundary>} />
                <Route path="charts" element={<ChartingPage />} />
                <Route path="market-monitor" element={<AISignalsPage />} />
                <Route path="ai-signals" element={<AISignalsPage />} />
                <Route path="trading-history" element={<ErrorBoundary><TradingHistoryPage /></ErrorBoundary>} />
                <Route path="brokers" element={<BrokersPage />} />
                <Route
                  path="analytics"
                  element={
                    <Navigate
                      to={
                        getLegacyAnalyticsDestination(user?.role)
                      }
                      replace
                    />
                  }
                />
                <Route path="paper-trading" element={<ErrorBoundary><PaperTradingPage /></ErrorBoundary>} />
                <Route path="portfolio" element={<ErrorBoundary><PortfolioPage /></ErrorBoundary>} />
                <Route path="settings" element={<ErrorBoundary><SettingsPage /></ErrorBoundary>} />
                <Route path="profile" element={<ErrorBoundary><ProfilePage /></ErrorBoundary>} />
                <Route path="contract-rollover" element={<ContractRolloverPage />} />
                <Route path="arbitrage" element={<ErrorBoundary><ArbitragePage /></ErrorBoundary>} />
                <Route path="journal" element={<JournalPage />} />
                <Route
                  path="screener"
                  element={
                    <FeatureRoute feature={DASHBOARD_FEATURES.screener}>
                      <ScreenerPage />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="oi-analysis"
                  element={
                    <FeatureRoute feature={DASHBOARD_FEATURES.oiAnalysis}>
                      <OIAnalysisPage />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="options"
                  element={
                    <FeatureRoute feature={DASHBOARD_FEATURES.optionsBuilder}>
                      <OptionsBuilderPage />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="options-analytics"
                  element={
                    <FeatureRoute feature={DASHBOARD_FEATURES.optionsAnalytics}>
                      <ErrorBoundary>
                        <OptionsAnalyticsPage />
                      </ErrorBoundary>
                    </FeatureRoute>
                  }
                />
                <Route
                  path="marketplace"
                  element={
                    <FeatureRoute feature={DASHBOARD_FEATURES.marketplace}>
                      <MarketplacePage />
                    </FeatureRoute>
                  }
                />
                <Route path="code-editor" element={<CodeEditorPage />} />
                <Route path="assistant" element={<ErrorBoundary><AIChatPage /></ErrorBoundary>} />
                <Route path="ai-chat" element={<ErrorBoundary><AIChatPage /></ErrorBoundary>} />
                <Route path="backtest-compare" element={<BacktestComparisonPage />} />
                <Route path="algo-compare" element={<AlgoComparisonPage />} />
                <Route path="correlation" element={<CorrelationMatrixPage />} />
                <Route path="activity" element={<AuditLogPage />} />
                <Route
                  path="status"
                  element={
                    <FeatureRoute feature={DASHBOARD_FEATURES.statusPage}>
                      <StatusPage />
                    </FeatureRoute>
                  }
                />
                <Route path="calculators" element={<CalculatorsPage />} />
                <Route
                  path="api-playground"
                  element={
                    <FeatureRoute feature={DASHBOARD_FEATURES.apiPlayground}>
                      <ApiPlaygroundPage />
                    </FeatureRoute>
                  }
                />
                <Route path="compliance" element={<CompliancePage />} />
                <Route path="conditional-orders" element={<ConditionalOrdersPage />} />
                <Route path="webhooks" element={<ErrorBoundary><WebhooksPage /></ErrorBoundary>} />
                <Route path="tradingview-webhooks" element={<ErrorBoundary><TradingViewWebhookPage /></ErrorBoundary>} />
                <Route
                  path="x3-connect"
                  element={
                    <FeatureRoute feature={DASHBOARD_FEATURES.mcpManagement}>
                      <ErrorBoundary>
                        <MCPManagementPage />
                      </ErrorBoundary>
                    </FeatureRoute>
                  }
                />
                <Route path="mcp" element={<Navigate to="/x3-connect" replace />} />
                <Route path="ai-usage" element={<AIUsagePage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
              <Route
                path="admin"
                element={
                  <AdminSessionProvider>
                    <AdminRouteGuard />
                  </AdminSessionProvider>
                }
              >
                <Route element={<AdminLayout />}>
                  {getAdminRouteElements()}
                  {getAdminCompatibilityRouteElements()}
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Route>
              <Route
                path="ai-signals/admin"
                element={<Navigate to="/admin/ai/market-monitor" replace />}
              />
            </Routes>
          </PageTitleProvider>
        </WebSocketProvider>
      </LiveAnnouncerProvider>
    </ErrorBoundary>
  )
}

export default App
